import React, { useEffect } from 'react';
import { Alert, Button, Link, Typography } from '@evaneos/b2b-ui';
import { IntlShape, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { DossierId, Participants } from '../../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { AI } from '../../../../design-system/Icons/AI';

import style from './GenerateAT1Alert.css';

type Props = {
    participants: Participants;
    status: 'idle' | 'loading' | 'success' | 'error';
    onClick: () => void;
};

export function GenerateAT1Alert({ participants, status, onClick }: Props) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const isFamily = checkIfIsFamily(participants);

    useEffect(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.generateAt1Displayed({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {status === 'error' && (
                <Alert severity="error" sx={{ maxWidth: '100%', mb: 1, display: 'flex' }}>
                    <Typography variant="body1" color="error">
                        {formatMessage({
                            id: 'dossier.discussion.generated_at1.information.generation_error',
                        })}
                    </Typography>
                </Alert>
            )}
            <Alert
                severity="ai"
                sx={{
                    maxWidth: '100%',
                    mb: 1,
                    display: 'flex',
                    '& .MuiAlert-message': { width: '100%' },
                }}
            >
                {isFamily ? (
                    <>
                        {formatMessage(
                            {
                                id: 'dossier.discussion.generated_at1.information.description.part_1.family',
                            },
                            {
                                totalChildrenParticipants:
                                    getTotalChildrenParticipants(participants),
                                span: (chunks: React.ReactNode) => <b>{chunks}</b>,
                            },
                        )}{' '}
                        <b>({formatAges(participants, formatMessage)})</b>.
                    </>
                ) : (
                    formatMessage(
                        {
                            id: 'dossier.discussion.generated_at1.information.description.part_1.not_family',
                        },
                        {
                            count: participants.number.adults,
                        },
                    )
                )}
                <br />
                {formatMessage(
                    {
                        id: 'dossier.discussion.generated_at1.information.description.part_2',
                    },
                    {
                        span: (chunks: React.ReactNode) => <b>{chunks}</b>,
                    },
                )}
                <div className={style.bottom}>
                    <Link
                        href="https://evaneos.typeform.com/to/g4WqVZJF"
                        sx={{
                            color: 'text.ai',
                            fontWeight: 700,
                            '&:hover': {
                                color: 'text.ai',
                            },
                        }}
                        target="_blank"
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.giveFeedbackClicked({
                                    ...defaultProperties,
                                    answer: 'Yes',
                                    is_at1_auto: false,
                                });
                            });
                        }}
                    >
                        {formatMessage({
                            id: 'dossier.discussion.generated_at1.information.link',
                        })}
                    </Link>
                    <Button
                        color="ai"
                        sx={{ whiteSpace: 'nowrap' }}
                        startIcon={
                            <AI
                                color="var(--white)"
                                width={16}
                                height={16}
                                className={status === 'loading' ? style.spin : ''}
                            />
                        }
                        disabled={status === 'loading'}
                        onClick={onClick}
                    >
                        {status === 'loading'
                            ? formatMessage({
                                  id: 'dossier.discussion.generated_at1.information.button.loading',
                              })
                            : formatMessage({
                                  id: 'dossier.discussion.generated_at1.information.button.label',
                              })}
                    </Button>
                </div>
            </Alert>
        </>
    );
}

function checkIfIsFamily(participants: Participants) {
    return (
        participants.number.babies > 0 ||
        participants.number.children > 0 ||
        participants.number.teenagers > 0
    );
}

function formatAges(participants: Participants, formatMessage: IntlShape['formatMessage']) {
    const babiesAges = participants.childrenAges?.babies || [];
    const childrenAges = participants.childrenAges?.children || [];
    const teenagersAges = participants.childrenAges?.teenagers || [];

    const ages = [...babiesAges, ...childrenAges, ...teenagersAges];
    const formattedAges = ages
        .map((age) =>
            formatMessage(
                { id: 'dossier.discussion.generated_at1.information.description.age' },
                {
                    age,
                },
            ),
        )
        .join(', ');

    return formattedAges;
}

function getTotalChildrenParticipants(participants: Participants) {
    const babiesCount = participants.number.babies;
    const childrenCount = participants.number.children;
    const teenagersCount = participants.number.teenagers;

    return babiesCount + childrenCount + teenagersCount;
}
